import { Component, OnInit, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { 
    MarvInputComponent, MarvSelectComponent, MarvSelectItem, MarvInputDateComponent, MarvTextareaComponent, 
    MarvInputFileComponent, MarvInputFile, MarvFieldComponent, MarvButtonComponent
} from '@marv/components';
import { 
    MissionQuestionnaire, MissionQuestionnaireField, MissionQuestionnaireInputRule, 
    QuestionnaireValues, SRSectionInputModel 
} from '@app/models';

@Component({
    selector: 'app-mission-questionnaire',
    templateUrl: './questionnaire.component.html',
    styleUrls: ['./questionnaire.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvFieldComponent), forwardRef(() => MarvButtonComponent), forwardRef(() => MarvInputComponent), 
        forwardRef(() => MarvSelectComponent), forwardRef(() => MarvInputDateComponent), forwardRef(() => MarvTextareaComponent), 
        forwardRef(() => MarvInputFileComponent)
    ]
})
export class MissionQuestionnaireComponent  implements OnInit {

    @Output() onSaveQuestionnaire = new EventEmitter<QuestionnaireValues|null>();
   
    isFetchingSave: boolean = false;
    section: SRSectionInputModel;
    questionnaire: MissionQuestionnaire|null = null;
    numberOfMetaRanks: Array<number> = [];
    disabledForm: boolean = false;
    questionnaireForm: FormGroup = new FormGroup({});
    fieldsMap: Map<string, MissionQuestionnaireField> = new Map<string, MissionQuestionnaireField>;
    fieldsPhotos: {[key: string]: MarvInputFile} = {};
    fieldsVisibility: Map<string, boolean> = new Map<string, boolean>;
    fieldsSubscription: Map<string, Subscription> = new Map<string, Subscription>;
    booleanOptions: Array<MarvSelectItem> = [
        new MarvSelectItem(this.translateService.instant('YES'), true),
        new MarvSelectItem(this.translateService.instant('NO'), false)
    ];    
    mapOptions: Map<string, MarvSelectItem[]> = new Map<string, MarvSelectItem[]>;
    isaRules: Array<MissionQuestionnaireInputRule> = [];
    questionnaireValuesChanged: boolean = false;
    photosFileExt: Array<string> = ['png','jpeg'];

    constructor(
        private translateService: TranslateService
    ) { }

    ngOnInit() {
        
        let questionnaire: MissionQuestionnaire = new MissionQuestionnaire();
        let questionnaireForm: FormGroup = new FormGroup({});
        let isaRules: Array<MissionQuestionnaireInputRule> = [];
        let fieldsMap: Map<string, MissionQuestionnaireField> = new Map<string, MissionQuestionnaireField>;
        let fieldsPhotos: {[key: string]: MarvInputFile} = {};
        let mapOptions: Map<string, MarvSelectItem[]> = new Map<string, MarvSelectItem[]>;
        
        this.questionnaireValuesChanged = false;
        
        if(questionnaire !== null){  
        
            for (let r of this.section.rules){
                if (r.scope === 'ISA') {
                    isaRules = isaRules.concat(questionnaire.extractIsaInputRuleFromDef(r));
                }
            }
                 
            for(let i of this.numberOfMetaRanks){
                for(let group of questionnaire.groups){
                    if(group.availableOnMetaRank != null && group.availableOnMetaRank.includes(i)){
                        for(let field of group.datafields){
                            if(field.metaRank == i){    
                             
                                fieldsMap.set(field.code, field);
                                fieldsPhotos[field.code] = new MarvInputFile();
                                if(['ENUM', 'MCQ'].indexOf(field.type) !== -1){
                                    let items: Array<MarvSelectItem> = [];
                                    field.options.forEach(o => {
                                        items.push(new MarvSelectItem(o.label, o.value));
                                    });
                                    mapOptions.set(field.code, items);
                                }
                                
                                questionnaireForm.addControl(field.code, new FormControl(null), {emitEvent: false}); 
                                this.fieldsSubscription.set(field.code,
                                    questionnaireForm.controls[field.code].valueChanges.subscribe(value => {                      
                                        this.onUpdateFieldValue(field.code);
                                    })
                                );
                            }
                        }
                    }
                }
            }            
        }
        
        this.fieldsMap = fieldsMap;
        this.fieldsPhotos = fieldsPhotos;
        this.mapOptions = mapOptions;
        this.questionnaire = questionnaire;
        this.questionnaireForm = questionnaireForm;
        this.isaRules = isaRules;
    }
    
    ngOnDestroy() { 
        this.fieldsSubscription.forEach(s => {
            s.unsubscribe();
        });
        this.fieldsSubscription.clear();
    }

    shouldUseTextArea(field: MissionQuestionnaireField): boolean {
        if (field == null || field.dataFormat == null) return false;
        if (field.dataFormat.toLowerCase().indexOf("multibarcode") >= 0) return true;
        if (field.dataFormat.toLowerCase().indexOf("multiline") >= 0) return true;
        return false;
    }
    
    onUpdateFieldValue(codeField: string){
       
        let field = this.fieldsMap.get(codeField);
       
        if(field){
       
            this.questionnaireValuesChanged = true;

            let v = this.questionnaireForm.controls[field.code].value;

            let matchingRules = this.isaRules.filter(r => r.trigger == field.fqCode);
            matchingRules = matchingRules.filter(r => r.operator.matches(r.value, v));

            for (let r of matchingRules) {

                for(let s of r.forbidden){
                    let f = this.fieldsMap.get(codeField);
                    if(f){
                        this.fieldsVisibility.set(s,false)
                        this.questionnaireForm.controls[s].setValue(null);
                        this.questionnaireForm.controls[s].removeValidators(Validators.required);
                    }
                }
                for(let s of r.optional){
                    let f = this.fieldsMap.get(codeField);
                    if(f){
                        this.fieldsVisibility.set(s,true);
                        if(f.mandatory){
                            this.questionnaireForm.controls[s].addValidators(Validators.required);
                        }
                        else{
                            this.questionnaireForm.controls[s].removeValidators(Validators.required);
                        }
                    }
                }
                for(let s of r.mandatory){
                    let f = this.fieldsMap.get(codeField);
                    if(f){
                        this.fieldsVisibility.set(s,true);
                        if(f.mandatory){
                            this.questionnaireForm.controls[s].addValidators(Validators.required);
                        }
                        else{
                            this.questionnaireForm.controls[s].removeValidators(Validators.required);
                        }
                    }
                }

            }
        }
    }
    
    saveQuestionnaire(){
        
         this.isFetchingSave = true;
        
        if(this.questionnaireForm.valid){

            let values: QuestionnaireValues = {};

            this.fieldsMap.forEach(field => {
                if(field.type === 'PHOTO'){
                    if(this.fieldsPhotos[field.code]){
                        values[field.code] = '';
                    }                    
                }
                else{
                    values[field.code] = this.questionnaireForm.controls[field.code].value;
                }
            });

            this.onSaveQuestionnaire.emit(values);
        }
        else{
            for (var controlName in this.questionnaireForm.controls) {
                if(!this.questionnaireForm.controls[controlName].valid) {
                    this.questionnaireForm.controls[controlName].markAsTouched();
                }
            }
        }
        
         this.isFetchingSave = false;
    }
    
    cancelQuestionnaire(){
        this.onSaveQuestionnaire.emit(null);
    }
}
